import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Bell, X } from 'lucide-react';
import { Button } from './ui/button';
import { usePushNotifications } from '../hooks/usePushNotifications';
import { toast } from 'sonner';

export function PushNotificationBanner() {
  const { t } = useTranslation();
  const { permission, isSubscribed, isSupported, subscribe, loading } = usePushNotifications();
  const [visible, setVisible] = useState(false);
  const [subscribing, setSubscribing] = useState(false);

  useEffect(() => {
    if (!isSupported) return;
    if (isSubscribed) return;
    if (permission === 'denied') return;
    const dismissed = localStorage.getItem('push_banner_dismissed');
    if (dismissed) {
      const dismissedAt = parseInt(dismissed, 10);
      if (Date.now() - dismissedAt < 7 * 24 * 60 * 60 * 1000) return;
    }
    const timer = setTimeout(() => setVisible(true), 5000);
    return () => clearTimeout(timer);
  }, [isSupported, isSubscribed, permission]);

  const handleSubscribe = async () => {
    setSubscribing(true);
    try {
      const result = await subscribe();
      if (result.success) {
        toast.success(t('pushSuccess') || 'Notifiche attivate!');
        setVisible(false);
      } else {
        // Errori comuni con messaggi chiari
        const reason = result.reason || 'unknown';
        let msg = t('pushErrorGeneric') || 'Impossibile attivare le notifiche';
        if (reason === 'denied') {
          msg = t('pushErrorDenied') || 'Permessi negati dal browser. Controlla le impostazioni del sito.';
          setVisible(false);
        } else if (reason === 'unsupported') {
          msg = t('pushErrorUnsupported') || 'Il tuo browser non supporta le notifiche push';
          setVisible(false);
        } else {
          msg = `${msg}: ${reason}`;
        }
        toast.error(msg);
        console.error('[push-banner] subscribe failed:', reason);
      }
    } catch (e) {
      console.error('[push-banner] exception:', e);
      toast.error(t('pushErrorGeneric') || 'Errore attivazione notifiche');
    } finally {
      setSubscribing(false);
    }
  };

  const handleDismiss = () => {
    setVisible(false);
    localStorage.setItem('push_banner_dismissed', String(Date.now()));
  };

  if (!visible) return null;

  return (
    <div data-testid="push-notification-banner"
      className="fixed bottom-4 left-4 right-4 sm:left-auto sm:right-4 sm:max-w-sm z-50 animate-in slide-in-from-bottom-4 fade-in duration-300">
      <div className="bg-card border border-border/80 rounded-2xl shadow-xl p-4">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-xl bg-red-500/10 flex items-center justify-center shrink-0">
            <Bell size={20} className="text-red-500" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-semibold text-sm mb-0.5">{t('pushBannerTitle')}</p>
            <p className="text-xs text-muted-foreground leading-relaxed">{t('pushBannerDesc')}</p>
            <div className="flex items-center gap-2 mt-3">
              <Button data-testid="enable-push-btn" size="sm" onClick={handleSubscribe}
                disabled={subscribing || loading} className="text-xs h-8 px-4 bg-red-500 hover:bg-red-600 text-white">
                {subscribing ? t('pushBannerActivating') : t('pushBannerActivate')}
              </Button>
              <button data-testid="dismiss-push-btn" onClick={handleDismiss}
                className="text-xs text-muted-foreground hover:text-foreground transition-colors">
                {t('pushBannerDismiss')}
              </button>
            </div>
          </div>
          <button onClick={handleDismiss} className="p-1 rounded-lg hover:bg-muted transition-colors shrink-0">
            <X size={14} className="text-muted-foreground" />
          </button>
        </div>
      </div>
    </div>
  );
}
